import { Role, type GameState, type Player } from '@susdle/shared';
import type {
  RoleRevealView, GhostGuessingView, GuessRevealView,
  DiscussionPlayer, RevealedRole,
} from '../discussion/types.js';
import type { SeatPlayer } from '../shared/components/AvatarFrame.js';
import { classifyAvatarUrl } from './stateToView.js';

export interface RevealMappingContext {
  meId: string;
}

function seatFromPlayer(player: Player): SeatPlayer {
  const avatar = classifyAvatarUrl(player.avatarUrl);
  const seat: SeatPlayer = {
    id: player.id,
    name: player.name,
    avatarUrl: player.avatarUrl,
    isDefaultAvatar: avatar.isDefaultAvatar,
  };
  if (avatar.defaultAvatarIndex !== undefined) seat.defaultAvatarIndex = avatar.defaultAvatarIndex;
  return seat;
}

function castPlayer(player: Player): DiscussionPlayer {
  return {
    ...seatFromPlayer(player),
    turnOrder: player.turnOrder,
    connected: player.connected,
    clue: player.clue === '' ? null : player.clue,
  };
}

/** Alive players + the just-struck pending player, sorted by turnOrder. Prior-
 *  round dead are excluded (not alive, not the current pending id). */
function revealArcCast(state: GameState): DiscussionPlayer[] {
  const pendingId = state.pendingEliminationId;
  const cast: DiscussionPlayer[] = [];
  for (const [, p] of state.players) {
    if (p.alive || p.id === pendingId) cast.push(castPlayer(p));
  }
  cast.sort((a, b) => a.turnOrder - b.turnOrder);
  return cast;
}

/** `'ghost'` iff the local viewer's own role is Ghost (SELF-tagged), else `'village'`. */
function revealPov(state: GameState, meId: string): 'village' | 'ghost' {
  return state.players.get(meId)?.role === Role.Ghost ? 'ghost' : 'village';
}

const MISSING_SEAT = (id: string): SeatPlayer => ({ id, name: '', avatarUrl: '', isDefaultAvatar: true });

export function stateToRoleRevealView(state: GameState, ctx: RevealMappingContext): RoleRevealView {
  const pendingId = state.pendingEliminationId;
  const elim = state.players.get(pendingId);
  return {
    meId: ctx.meId,
    cast: revealArcCast(state),
    // Defensive: pendingEliminationId always points at a real player during
    // RoleReveal (the vanished-target path skips this phase server-side).
    eliminated: elim
      ? { player: seatFromPlayer(elim), role: (elim.revealedRole || 'villager') as RevealedRole }
      : { player: MISSING_SEAT(pendingId), role: 'villager' },
    // AFK also-eliminated (multi-strike) deferred — see plan §Deferred.
    alsoEliminated: [],
  };
}

export function stateToGhostGuessingView(state: GameState, ctx: RevealMappingContext): GhostGuessingView {
  const me = state.players.get(ctx.meId);
  const pendingId = state.pendingEliminationId;
  const ghost = state.players.get(pendingId);
  return {
    meId: ctx.meId,
    pov: revealPov(state, ctx.meId),
    cast: revealArcCast(state),
    guessingGhost: ghost ? seatFromPlayer(ghost) : MISSING_SEAT(pendingId),
    isMine: ctx.meId === pendingId,
    // SELF-tagged; only consumed on the guessing Ghost's own (isMine) view.
    secretLength: me?.secretWordLength ?? 0,
    phaseEndsAt: state.phaseEndsAt === 0 ? null : state.phaseEndsAt,
    durationMs: state.phaseDurationMs,
  };
}

export function stateToGuessRevealView(state: GameState, ctx: RevealMappingContext): GuessRevealView {
  const pendingId = state.pendingEliminationId;
  const ghost = state.players.get(pendingId);
  return {
    meId: ctx.meId,
    pov: revealPov(state, ctx.meId),
    cast: revealArcCast(state),
    guessingGhost: ghost ? seatFromPlayer(ghost) : MISSING_SEAT(pendingId),
    guess: state.ghostGuessText, // PUBLIC
  };
}
